import { CaretDown, CaretUp, ChatCircleText, CheckCircle, Clock, WarningCircle } from "@phosphor-icons/react";
import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import CustomText from "../../../../components/common/general/CustomText/CustomText";
import { colors, spacing } from "../../../../styles/design-system";
import { formatDurationFromSeconds } from "../../../../utils/timeUtils";
import InsightsCallout from "./InsightsCallout";
import { type ObjectiveAnalytics } from "./insightsTypes";

type ModuleInsightsObjectiveItemProps = {
  objective: ObjectiveAnalytics;
  objectiveIndex: number;
  defaultExpanded?: boolean;
};

const ModuleInsightsObjectiveItem: React.FC<ModuleInsightsObjectiveItemProps> = ({
  objective,
  objectiveIndex,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const progress = objective.progress_details;
  const summaryLines = useMemo(() => (progress?.summary ? [progress.summary] : []), [progress?.summary]);
  const successLines = useMemo(
    () => progress?.success_points?.filter(Boolean).map(String) ?? [],
    [progress?.success_points]
  );
  const confusionLines = useMemo(
    () => progress?.confusion_points?.filter(Boolean).map(String) ?? [],
    [progress?.confusion_points]
  );
  const recommendationLines = useMemo(
    () => progress?.further_recommendations?.filter(Boolean).map(String) ?? [],
    [progress?.further_recommendations]
  );
  const leftBorderColor =
    objective.confusion_points >= 3
      ? colors.error[300]
      : objective.success_points > 0
        ? colors.success[300]
        : colors.grey[200];
  const averageTimeText =
    typeof objective.average_time_taken === "number" ? formatDurationFromSeconds(objective.average_time_taken) : "—";

  return (
    <View style={[styles.container, { borderLeftColor: leftBorderColor }]}>
      <Pressable style={styles.header} onPress={() => setExpanded((prev) => !prev)}>
        <View style={styles.headerText}>
          <CustomText
            text={`${objectiveIndex}. ${objective.label}`}
            size="s"
            weight="medium"
            useTranslationText={false}
          />
          <View style={styles.metaRow}>
            <View style={styles.metaItem}>
              <CheckCircle size={12} color={colors.success[600]} />
              <CustomText
                text={`${objective.success_points} success`}
                size="xs"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
            <View style={styles.metaItem}>
              <WarningCircle size={12} color={colors.error[600]} />
              <CustomText
                text={`${objective.confusion_points} confusion`}
                size="xs"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
            <View style={styles.metaItem}>
              <ChatCircleText size={12} color={colors.grey[500]} />
              <CustomText
                text={`${progress?.questions_asked ?? 0} qs`}
                size="xs"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
            <View style={styles.metaItem}>
              <Clock size={12} color={colors.grey[500]} />
              <CustomText text={averageTimeText} size="xs" useTranslationText={false} style={styles.mutedText} />
            </View>
          </View>
        </View>
        {expanded ? <CaretUp size={14} color={colors.grey[500]} /> : <CaretDown size={14} color={colors.grey[500]} />}
      </Pressable>

      {expanded ? (
        <View style={styles.content}>
          <InsightsCallout title="AI Summary" lines={summaryLines} tone="brand" variant="rail" />
          <InsightsCallout title="What Went Well" lines={successLines} tone="success" variant="rail" />
          <InsightsCallout title="Areas of Confusion" lines={confusionLines} tone="warning" variant="rail" />
          <InsightsCallout title="AI Recommendations" lines={recommendationLines} tone="brand" variant="rail" />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderLeftWidth: 2,
    borderRadius: 10,
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.xs,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  mutedText: {
    color: colors.grey[500],
  },
  content: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    gap: spacing.xs,
  },
});

export default ModuleInsightsObjectiveItem;
